import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Fingerprint, Trash2, Plus, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import fingerprintApi, { FingerprintDevice } from '../services/fingerprintApi';
import toast from 'react-hot-toast';
import FingerprintEnrollmentModal from '../components/FingerprintEnrollmentModal';

interface Employee {
  id: number;
  userId: number;
  hasFingerprint: boolean;
  fingerprintId: number | null;
  fingerprintEnrolledAt: string | null;
  user: {
    id: number;
    name: string;
    email: string;
  };
}

const FingerprintManagement: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [enrollmentModalOpen, setEnrollmentModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [deletingEmployee, setDeletingEmployee] = useState<Employee | null>(null);
  const [deleteDeviceId, setDeleteDeviceId] = useState<number | null>(null);

  // Get available devices for delete operation
  const { data: deleteDevicesData } = useQuery({
    queryKey: ['fingerprint-devices-my'],
    queryFn: () => fingerprintApi.getMyFingerprintDevices(),
    enabled: !!deletingEmployee
  });

  const deleteDevices: FingerprintDevice[] = deleteDevicesData?.devices || [];

  // Check if fingerprint module is enabled
  const { data: enabledResponse } = useQuery({
    queryKey: ['fingerprint-enabled'],
    queryFn: () => fingerprintApi.checkFingerprintEnabled()
  });

  const isEnabled = enabledResponse?.enabled || false;

  // Fetch employees
  const { data: employeesResponse, isLoading } = useQuery({
    queryKey: ['employees'],
    queryFn: () => apiService.get('/employees')
  });

  const employees: Employee[] = employeesResponse?.employees || [];

  // Delete fingerprint mutation
  const deleteMutation = useMutation({
    mutationFn: ({ employeeId, deviceId }: { employeeId: number; deviceId: number }) => 
      fingerprintApi.deleteFingerprint(employeeId, { deviceId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      toast.success('Fingerprint deleted successfully');
      setDeletingEmployee(null);
      setDeleteDeviceId(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete fingerprint');
    }
  });

  const handleEnroll = (employee: Employee) => {
    setSelectedEmployee(employee);
    setEnrollmentModalOpen(true);
  };

  const handleDelete = (employee: Employee) => {
    setDeletingEmployee(employee);
  };

  const confirmDelete = () => {
    if (deletingEmployee && deleteDeviceId) {
      deleteMutation.mutate({ 
        employeeId: deletingEmployee.id, 
        deviceId: deleteDeviceId 
      });
    } else if (!deleteDeviceId) {
      toast.error('Please select a device');
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  if (!isEnabled) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Fingerprint Module Disabled</h1>
          <p className="text-gray-600">
            The fingerprint management module is not enabled for your account.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Fingerprint Management</h1>
        <p className="text-gray-600">Manage fingerprint enrollment for employees</p>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employee
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fingerprint Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fingerprint ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Enrolled At
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {employees.map((employee) => (
                <tr key={employee.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{employee.user.name}</div>
                    <div className="text-sm text-gray-500">{employee.user.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {employee.hasFingerprint ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <Fingerprint className="w-3 h-3 mr-1" />
                        Enrolled
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        Not Enrolled
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {employee.fingerprintId || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(employee.fingerprintEnrolledAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {employee.hasFingerprint ? (
                      <button
                        onClick={() => handleDelete(employee)}
                        className="text-red-600 hover:text-red-900 mr-4"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    ) : (
                      <button
                        onClick={() => handleEnroll(employee)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        <Plus className="w-5 h-5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Enrollment Modal */}
      {enrollmentModalOpen && selectedEmployee && (
        <FingerprintEnrollmentModal
          employee={selectedEmployee}
          onClose={() => {
            setEnrollmentModalOpen(false);
            setSelectedEmployee(null);
            queryClient.invalidateQueries({ queryKey: ['employees'] });
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deletingEmployee && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Delete Fingerprint</h3>
              <p className="text-sm text-gray-500 mb-4">
                Are you sure you want to delete the fingerprint for {deletingEmployee.user.name}?
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Device:
                </label>
                <select
                  value={deleteDeviceId || ''}
                  onChange={(e) => setDeleteDeviceId(e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
                >
                  <option value="">-- Select Device --</option>
                  {deleteDevices.map((device) => (
                    <option key={device.id} value={device.deviceId}>
                      {device.name} (ID: {device.deviceId})
                    </option>
                  ))}
                </select>
                {deleteDevices.length === 0 && (
                  <p className="mt-1 text-xs text-gray-500">No devices available.</p>
                )}
              </div>
              <div className="flex justify-center gap-4 px-4 py-3">
                <button
                  onClick={() => {
                    setDeletingEmployee(null);
                    setDeleteDeviceId(null);
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={deleteMutation.isPending || !deleteDeviceId}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FingerprintManagement;

